import {
  PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
  PEG_BOARD_SELECTION_WINDOW_DEFAULT,
  clampPegBoardSelectionWindow,
  normalizePegBoardQueueReturn,
} from './constants';

const waitlistId = (entry) => {
  if (entry == null) return null;
  if (typeof entry === 'string') return entry;
  return entry.id || entry.player_id || null;
};

const uniqueIds = (ids) => {
  const seen = new Set();
  const out = [];
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
};

const normalizeGender = (value) => {
  const g = String(value || '').toLowerCase();
  if (g === 'male' || g === 'female') return g;
  return null;
};

/** Deterministic 0–1 PRNG from a string seed (mulberry32). */
export const hashSeedToUint32 = (seed) => {
  const str = String(seed ?? '');
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

export const mulberry32 = (a) => {
  let t = a >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

export const seededShuffle = (items, seed) => {
  const arr = [...items];
  const rand = mulberry32(hashSeedToUint32(seed));
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

/**
 * Front of waitlist only.
 * @param {Array<string|{id?:string}>} waitlist
 * @returns {string|null}
 */
export const getPicker = (waitlist = []) => {
  if (!Array.isArray(waitlist) || waitlist.length === 0) return null;
  return waitlistId(waitlist[0]);
};

const slotPlayerCount = (row) =>
  (Array.isArray(row?.team1) ? row.team1.length : 0) +
  (Array.isArray(row?.team2) ? row.team2.length : 0);

/**
 * First empty slot a Peg Board pick fills: lowest empty active court, else first empty next-up.
 */
export const getPegBoardPickTarget = ({ courtAssignments = [], nextAssignments = [] } = {}) => {
  const emptyActive = [...(courtAssignments || [])]
    .filter((court) => slotPlayerCount(court) === 0 && court?.courtNumber != null)
    .sort((a, b) => Number(a.courtNumber) - Number(b.courtNumber))[0];
  if (emptyActive) {
    return { kind: 'active', courtNumber: Number(emptyActive.courtNumber) };
  }

  const emptyNext = [...(nextAssignments || [])]
    .map((match, matchIndex) => ({ match, matchIndex }))
    .filter(({ match }) => slotPlayerCount(match) === 0)
    .sort((a, b) => {
      const aNum = a.match?.courtNumber != null ? Number(a.match.courtNumber) : a.matchIndex;
      const bNum = b.match?.courtNumber != null ? Number(b.match.courtNumber) : b.matchIndex;
      return aNum - bNum;
    })[0];
  if (emptyNext) {
    return { kind: 'next_up', matchIndex: emptyNext.matchIndex };
  }
  return null;
};

export const formatPegBoardPickTargetLabel = (target) => {
  if (!target) return '';
  if (target.kind === 'active') return `Court ${target.courtNumber}`;
  return `Upcoming Match #${(target.matchIndex ?? 0) + 1}`;
};

export const isPegBoardPickTargetActive = (target, courtNumber) =>
  target?.kind === 'active' && Number(target.courtNumber) === Number(courtNumber);

export const isPegBoardPickTargetNextUp = (target, matchIndex) =>
  target?.kind === 'next_up' && Number(target.matchIndex) === Number(matchIndex);

/**
 * Picker plus the next `window - 1` waitlist players.
 * @returns {{ pickerId: string|null, eligibleIds: string[], canPick: boolean, window: number }}
 */
export const getEligiblePool = (waitlist = [], window = PEG_BOARD_SELECTION_WINDOW_DEFAULT) => {
  const ids = uniqueIds((waitlist || []).map(waitlistId));
  const size = clampPegBoardSelectionWindow(window);
  const eligibleIds = ids.slice(0, size);
  const pickerId = eligibleIds[0] || null;
  return {
    pickerId,
    eligibleIds,
    canPick: eligibleIds.length >= 4,
    window: size,
  };
};

/**
 * Split court/upcoming "+ Add Player" candidates for Peg Board: picker window first
 * (waitlist positions 1..window), then the rest of the waitlist, then anyone else
 * in the pick pool (typically already on next-on-court).
 *
 * Queue numbers stay waitlist positions, even if someone ahead is already on court.
 */
export const groupPlayersForPegBoardCourtAdd = ({
  waitlist = [],
  selectionWindow,
  availablePlayers = [],
} = {}) => {
  const waitlistIds = uniqueIds((waitlist || []).map(waitlistId));
  const windowSize = clampPegBoardSelectionWindow(selectionWindow);
  const byId = new Map((availablePlayers || []).filter(Boolean).map((p) => [p.id, p]));
  const windowPlayers = [];
  const restPlayers = [];
  const seen = new Set();

  waitlistIds.forEach((id, index) => {
    const player = byId.get(id);
    if (!player) return;
    seen.add(id);
    const row = { player, queuePosition: index + 1, isPicker: index === 0 };
    if (index < windowSize) windowPlayers.push(row);
    else restPlayers.push(row);
  });

  const extraPlayers = (availablePlayers || []).filter((p) => p && !seen.has(p.id));
  return { windowPlayers, restPlayers, extraPlayers, windowSize };
};

/**
 * Validate a doubles selection: picker included, 4 unique, all eligible.
 * Occupied ids (on court / next_up) are rejected when provided.
 */
export const validateSelection = ({
  pickerId,
  partnerId,
  opp1,
  opp2,
  eligibleIds = [],
  occupiedIds = [],
  hostOverride = false,
} = {}) => {
  const selected = uniqueIds([pickerId, partnerId, opp1, opp2]);
  if (!pickerId) {
    return {
      ok: false,
      code: 'NO_PICKER',
      message: hostOverride
        ? 'Select four queue players, starting with team 1.'
        : 'No picker is at the front of the queue.',
    };
  }
  if (selected.length !== 4) {
    return { ok: false, code: 'NEED_FOUR', message: 'Select four unique players, including the picker.' };
  }
  if (!selected.includes(pickerId)) {
    return { ok: false, code: 'PICKER_REQUIRED', message: 'The picker must be in the game.' };
  }
  const eligible = new Set(eligibleIds);
  const notEligible = selected.filter((id) => !eligible.has(id));
  if (notEligible.length) {
    return {
      ok: false,
      code: 'NOT_ELIGIBLE',
      message: hostOverride
        ? 'Every selected player must be in the queue.'
        : 'Every selected player must be in the current picker window.',
    };
  }
  const occupied = new Set((occupiedIds || []).filter(Boolean));
  if (selected.some((id) => occupied.has(id))) {
    return {
      ok: false,
      code: 'PLAYER_OCCUPIED',
      message: 'A selected player is already on court or next up.',
    };
  }
  return {
    ok: true,
    team1: [pickerId, partnerId],
    team2: [opp1, opp2],
    playerIds: selected,
  };
};

/**
 * Put players back on the waitlist without finishing a game (court/upcoming X, clear, DnD off).
 * `toFront` is Peg Board: they skip the queue. Finished games still use appendReturningPlayers.
 */
export const returnPlayersToWaitlist = (waitlist = [], playerIds = [], { toFront = false } = {}) => {
  const incoming = uniqueIds((Array.isArray(playerIds) ? playerIds : [playerIds]).map(waitlistId));
  const rest = uniqueIds((waitlist || []).map(waitlistId)).filter((id) => !incoming.includes(id));
  if (!incoming.length) return rest;
  return toFront ? [...incoming, ...rest] : [...rest, ...incoming];
};

/**
 * Append finished players to the back of the remaining waitlist.
 * random: shuffle all four. winners_first: shuffle winners then losers.
 */
export const appendReturningPlayers = ({
  waitlist = [],
  team1 = [],
  team2 = [],
  winningTeam = 1,
  mode = PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST,
  seed = '0',
} = {}) => {
  const remaining = uniqueIds((waitlist || []).map(waitlistId));
  const t1 = uniqueIds((team1 || []).map(waitlistId));
  const t2 = uniqueIds((team2 || []).map(waitlistId));
  const finished = uniqueIds([...t1, ...t2]);
  const remainingWithoutFinished = remaining.filter((id) => !finished.includes(id));
  const normalizedMode = normalizePegBoardQueueReturn(mode);

  let returning;
  if (normalizedMode === PEG_BOARD_QUEUE_RETURN_WINNERS_FIRST) {
    const winners = winningTeam === 2 ? t2 : t1;
    const losers = winningTeam === 2 ? t1 : t2;
    returning = [
      ...seededShuffle(winners, `${seed}:w`),
      ...seededShuffle(losers, `${seed}:l`),
    ];
  } else {
    returning = seededShuffle(finished, `${seed}:r`);
  }

  return [...remainingWithoutFinished, ...returning];
};

const combinations = (items, k) => {
  const result = [];
  const rec = (start, chosen) => {
    if (chosen.length === k) {
      result.push([...chosen]);
      return;
    }
    for (let i = start; i < items.length; i += 1) {
      chosen.push(items[i]);
      rec(i + 1, chosen);
      chosen.pop();
    }
  };
  rec(0, []);
  return result;
};

const toPlayedKeySet = (playedKeys) => {
  if (playedKeys instanceof Set) return playedKeys;
  if (!playedKeys) return new Set();
  if (Array.isArray(playedKeys)) return new Set(playedKeys.filter(Boolean));
  return new Set();
};

/**
 * Canonical key for two doubles teams. Side order and player order within a team
 * do not matter (A+B vs C+D === C+D vs B+A).
 */
export const lineupKey = (team1 = [], team2 = []) => {
  const pair = (team) => uniqueIds((team || []).map(waitlistId)).sort();
  const a = pair(team1);
  const b = pair(team2);
  if (a.length !== 2 || b.length !== 2) return null;
  if (uniqueIds([...a, ...b]).length !== 4) return null;
  return [a.join('+'), b.join('+')].sort().join('|');
};

/**
 * Lineup keys from completed session games (`teams: [t1, t2]` or `team1`/`team2`).
 */
export const playedLineupKeys = (matchHistory = []) => {
  const keys = new Set();
  (matchHistory || []).forEach((game) => {
    const t1 = game?.team1 || game?.teams?.[0] || [];
    const t2 = game?.team2 || game?.teams?.[1] || [];
    const key = lineupKey(t1, t2);
    if (key) keys.add(key);
  });
  return keys;
};

const lineupGameType = (team1, team2, playersById = {}) => {
  const four = uniqueIds([...(team1 || []), ...(team2 || [])]);
  const genderOf = (id) => normalizeGender(playersById[id]?.gender);
  const males = four.filter((id) => genderOf(id) === 'male');
  const females = four.filter((id) => genderOf(id) === 'female');
  const t1Genders = (team1 || []).map(genderOf);
  const t2Genders = (team2 || []).map(genderOf);
  const evenMixed =
    males.length === 2 &&
    females.length === 2 &&
    t1Genders.includes('male') &&
    t1Genders.includes('female') &&
    t2Genders.includes('male') &&
    t2Genders.includes('female');
  if (evenMixed) return 'XD';
  if (males.length === 4) return 'MD';
  if (females.length === 4) return 'WD';
  return 'ANY';
};

const asLineup = (pickerId, partner, oppA, oppB, gameType) => ({
  ok: true,
  team1: [pickerId, partner],
  team2: [oppA, oppB],
  gameType,
});

const splitsWithPicker = (pickerId, restThree, gameType, playersById) =>
  restThree.map((partner, index) => {
    const opps = restThree.filter((_, i) => i !== index);
    const team1 = [pickerId, partner];
    const team2 = opps.slice(0, 2);
    return asLineup(
      pickerId,
      partner,
      team2[0],
      team2[1],
      gameType || lineupGameType(team1, team2, playersById)
    );
  });

const uniqueLineups = (lineups) => {
  const seen = new Set();
  const out = [];
  (lineups || []).forEach((lineup) => {
    const key = lineupKey(lineup?.team1, lineup?.team2);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(lineup);
  });
  return out;
};

const pickLineup = (candidates, seed) => {
  if (!candidates.length) return { ok: false, team1: [], team2: [], gameType: null };
  return seededShuffle(candidates, seed)[0];
};

const pickUnplayedThenFallback = (preferred, any, playedKeys, seed) => {
  const played = toPlayedKeySet(playedKeys);
  const unplayedPreferred = preferred.filter((c) => !played.has(lineupKey(c.team1, c.team2)));
  if (unplayedPreferred.length) return pickLineup(unplayedPreferred, seed);
  const unplayedAny = (any || []).filter((c) => !played.has(lineupKey(c.team1, c.team2)));
  if (unplayedAny.length) return pickLineup(unplayedAny, seed);
  return pickLineup(preferred, seed);
};

const enumeratePreferredWithPicker = (pickerId, others, playersById = {}) => {
  const genderOf = (id) => normalizeGender(playersById[id]?.gender);
  const pickerGender = genderOf(pickerId);
  const males = others.filter((id) => genderOf(id) === 'male');
  const females = others.filter((id) => genderOf(id) === 'female');

  if (pickerGender === 'male' && males.length >= 1 && females.length >= 2) {
    const lineups = [];
    females.forEach((partner) => {
      males.forEach((oppMale) => {
        females
          .filter((id) => id !== partner)
          .forEach((oppFemale) => {
            lineups.push(asLineup(pickerId, partner, oppMale, oppFemale, 'XD'));
          });
      });
    });
    return uniqueLineups(lineups);
  }
  if (pickerGender === 'female' && females.length >= 1 && males.length >= 2) {
    const lineups = [];
    males.forEach((partner) => {
      females.forEach((oppFemale) => {
        males
          .filter((id) => id !== partner)
          .forEach((oppMale) => {
            lineups.push(asLineup(pickerId, partner, oppMale, oppFemale, 'XD'));
          });
      });
    });
    return uniqueLineups(lineups);
  }
  if (pickerGender === 'male' && males.length >= 3) {
    return uniqueLineups(
      combinations(males, 3).flatMap((three) => splitsWithPicker(pickerId, three, 'MD', playersById))
    );
  }
  if (pickerGender === 'female' && females.length >= 3) {
    return uniqueLineups(
      combinations(females, 3).flatMap((three) => splitsWithPicker(pickerId, three, 'WD', playersById))
    );
  }
  return uniqueLineups(
    combinations(others, 3).flatMap((three) => {
      const arranged = arrangeEvenGenderDoubles(pickerId, three, playersById);
      return [{ ok: true, ...arranged }];
    })
  );
};

const enumerateAnyWithPicker = (pickerId, others, playersById = {}) =>
  uniqueLineups(
    combinations(others, 3).flatMap((three) => splitsWithPicker(pickerId, three, null, playersById))
  );

const enumerateFoursPreferred = (ids, playersById = {}) =>
  uniqueLineups(
    combinations(ids, 4).map((four) => {
      const arranged = arrangeEvenGenderDoubles(four[0], four.slice(1), playersById);
      return { ok: true, ...arranged };
    })
  );

const enumerateFoursAny = (ids, playersById = {}) =>
  uniqueLineups(
    combinations(ids, 4).flatMap((four) => splitsWithPicker(four[0], four.slice(1), null, playersById))
  );

/**
 * If the four players are 2 male + 2 female, pair the picker with the opposite
 * gender so both teams are mixed. Otherwise keep the given others as partner / opponents.
 */
export const arrangeEvenGenderDoubles = (pickerId, others = [], playersById = {}) => {
  const rest = uniqueIds((others || []).map(waitlistId)).filter((id) => id && id !== pickerId);
  const genderOf = (id) => normalizeGender(playersById[id]?.gender);
  const four = uniqueIds([pickerId, ...rest]);
  if (!pickerId || rest.length < 3 || four.length !== 4) {
    return { team1: [pickerId, rest[0]].filter(Boolean), team2: rest.slice(1, 3), gameType: 'ANY' };
  }

  const males = four.filter((id) => genderOf(id) === 'male');
  const females = four.filter((id) => genderOf(id) === 'female');
  if (males.length === 2 && females.length === 2) {
    const pickerGender = genderOf(pickerId);
    if (pickerGender === 'male' || pickerGender === 'female') {
      const partner = rest.find((id) => genderOf(id) && genderOf(id) !== pickerGender) || rest[0];
      const remaining = rest.filter((id) => id !== partner);
      return { team1: [pickerId, partner], team2: remaining.slice(0, 2), gameType: 'XD' };
    }
    return { team1: [males[0], females[0]], team2: [males[1], females[1]], gameType: 'XD' };
  }

  const gameType = males.length === 4 ? 'MD' : females.length === 4 ? 'WD' : 'ANY';
  return { team1: [pickerId, rest[0]], team2: rest.slice(1, 3), gameType };
};

/**
 * Suggest a doubles lineup that always includes the picker.
 * Prefers even mixed (1M+1F vs 1M+1F) when the window allows 2+2; otherwise MD / WD / any four.
 * Skips exact lineups already played this session unless no other option remains.
 */
export const suggestRandomDoubles = (
  eligible = [],
  pickerId,
  { playersById = {}, seed = 'suggest', playedKeys } = {}
) => {
  const ids = uniqueIds((eligible || []).map(waitlistId));
  if (!pickerId || !ids.includes(pickerId) || ids.length < 4) {
    return { ok: false, team1: [], team2: [], gameType: null };
  }

  const others = ids.filter((id) => id !== pickerId);
  const preferred = enumeratePreferredWithPicker(pickerId, others, playersById);
  if (!preferred.length) {
    return { ok: false, team1: [], team2: [], gameType: null };
  }
  const any = enumerateAnyWithPicker(pickerId, others, playersById);
  return pickUnplayedThenFallback(preferred, any, playedKeys, `${seed}:${pickerId}`);
};

/**
 * Host Random Pick with no picker selected: any four from the pool.
 * Same unplayed-then-fallback rule as suggestRandomDoubles.
 */
export const suggestRandomDoublesAnyPicker = (
  eligible = [],
  { playersById = {}, seed = 'suggest', playedKeys } = {}
) => {
  const ids = uniqueIds((eligible || []).map(waitlistId));
  if (ids.length < 4) {
    return { ok: false, team1: [], team2: [], gameType: null };
  }
  const preferred = enumerateFoursPreferred(ids, playersById);
  if (!preferred.length) {
    return { ok: false, team1: [], team2: [], gameType: null };
  }
  const any = enumerateFoursAny(ids, playersById);
  return pickUnplayedThenFallback(preferred, any, playedKeys, seed);
};
